import * as React from "react";
import { getImage, GatsbyImage, IGatsbyImageData } from "gatsby-plugin-image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { graphql, useStaticQuery } from "gatsby";

// Components
import Player from "components/UIElements/Player";
// Models
import { FileSystemNode } from "gatsby-source-filesystem";
import { IArtist } from "models/Artist";
import { ISite } from "models/Seo";
// Styles
import * as styles from "./SongMedia.module.css";
import { getNamesInLine } from "functions";

type SongMediaProps = {
  title: string;
  authors?: IArtist[];
  image?: {
    url: string;
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData;
    };
  };
  video?: string;
  artists?: IArtist[];
  covers?: string[];
  esp_srt?: FileSystemNode[];
  rom_srt?: FileSystemNode[];
};

const SongMedia: React.FC<SongMediaProps> = ({
  title,
  authors,
  image,
  video,
  artists,
  covers,
  esp_srt,
  rom_srt,
}) => {
  const [songNumber, setSongNumber] = React.useState<number>(0);
  const { site } = useStaticQuery(query) as ISite;
  const thumbnail = image
    ? getImage(image.childImageSharp.gatsbyImageData)
    : undefined;

  const songNumberHandler = (index: number) => {
    setSongNumber(index);
  };

  if (!video && !covers && !thumbnail) return <></>;
  if (!video && !covers && thumbnail) {
    return (
      <GatsbyImage
        className={styles.song__thumbnail}
        image={thumbnail}
        alt={title}
      />
    );
  }

  let video_subtitles_data: any = [];
  if (video && !covers) {
    // ADDING SUBS
    if (esp_srt && esp_srt.length > 0) {
      if (
        esp_srt[0] &&
        !(esp_srt[0].publicURL as string).includes("/empty.vtt")
      ) {
        video_subtitles_data.push({
          language: "Español",
          src: site.siteMetadata.siteUrl + esp_srt[0].publicURL,
          label: "Español",
          default: true,
        });
      }
    }
    if (rom_srt && rom_srt.length > 0) {
      if (
        rom_srt[0] &&
        !(rom_srt[0].publicURL as string).includes("/empty.vtt")
      ) {
        video_subtitles_data.push({
          language: "Romaji",
          src: site.siteMetadata.siteUrl + rom_srt[0].publicURL,
          label: "Romaji",
        });
      }
    }
    return (
      <Player thumb={image?.url} src={video} subtitles={video_subtitles_data} />
    );
  }

  if (covers && artists && authors && covers.length === artists.length) {
    const allVideos = video ? [video, ...covers] : covers;
    const allArtists = video ? [...artists] : artists;
    const author_names = getNamesInLine(authors);

    // ADDING SUBS
    if (esp_srt && esp_srt.length >= songNumber) {
      if (
        esp_srt[songNumber] &&
        !(esp_srt[songNumber].publicURL as string).includes("/empty.vtt")
      ) {
        video_subtitles_data.push({
          language: "Español",
          src: site.siteMetadata.siteUrl + esp_srt[songNumber].publicURL,
          label: "Español",
          default: true,
        });
      }
    }
    if (rom_srt && rom_srt.length >= songNumber) {
      if (
        rom_srt[songNumber] &&
        !(rom_srt[songNumber].publicURL as string).includes("/empty.vtt")
      ) {
        video_subtitles_data.push({
          language: "Romaji",
          src: site.siteMetadata.siteUrl + rom_srt[songNumber].publicURL,
          label: "Romaji",
          default: true,
        });
      }
    }

    return (
      <section>
        {songNumber <= allVideos.length && (
          <div style={{ position: "relative" }} key={allVideos[songNumber]}>
            <div className={styles.song__video_placeholder}></div>
            <div className={styles.song__video}>
              <Player
                thumb={image?.url}
                src={allVideos[songNumber]}
                subtitles={video_subtitles_data}
              />
            </div>
          </div>
        )}
        <ul className={styles.song__artists}>
          {video && (
            <li
              className={`${styles.song__artist_card}${
                songNumber === 0 ? ` ${styles.selected}` : ""
              }`}
              onClick={() => songNumberHandler(0)}
            >
              {authors.map((author) => {
                const authorAvatar = author.avatar
                  ? getImage(author.avatar.childImageSharp.gatsbyImageData)
                  : undefined;
                if (authorAvatar)
                  return (
                    <GatsbyImage
                      loading="eager"
                      key={author.name}
                      className={styles.song__artist_avatar}
                      image={authorAvatar}
                      alt={author.name}
                    />
                  );
                return <></>;
              })}
              <span className={styles.song__artist_name}>
                {author_names}
                <FontAwesomeIcon
                  className={styles.song__check_author}
                  icon={["fas", "check-circle"]}
                />
              </span>
            </li>
          )}

          {allArtists.map((artist, index) => {
            const artistAvatar = getImage(
              artist.avatar.childImageSharp.gatsbyImageData
            );
            const plus = video ? 1 : 0;
            return (
              <li
                className={`${styles.song__artist_card}${
                  index + plus === songNumber ? ` ${styles.selected}` : ""
                }`}
                key={artist.slug}
                onClick={() => songNumberHandler(index + plus)}
              >
                {artistAvatar && (
                  <GatsbyImage
                    loading="eager"
                    className={styles.song__artist_avatar}
                    image={artistAvatar}
                    alt={artist.name}
                  />
                )}
                <span className={styles.song__artist_name}>{artist.name}</span>
              </li>
            );
          })}
        </ul>
      </section>
    );
  }

  return <></>;
};

export default SongMedia;

const query = graphql`
  query {
    site {
      siteMetadata {
        siteUrl
      }
    }
  }
`;
